import { createElement, Fragment, type ReactElement, type ReactNode } from "react"
import { type ExternalToast, toast as sonnerToast } from "sonner"

import type { ToastCustomOptions, ToastOptions } from "./toast"

const DEFAULT_DURATION_MS = {
  success: 4000,
  info: 4000,
  error: 6000,
} as const

const buildToastOptions = (
  options: Omit<ToastOptions, "title">,
  duration: number
): ExternalToast => ({
  description: options.description,
  action: options.action,
  duration: options.duration ?? duration,
  id: options.id,
  dismissible: options.dismissible,
  testId: options.testId,
  unstyled: true,
})

const buildCustomOptions = (options?: ToastCustomOptions): ExternalToast => ({
  duration: options?.duration ?? DEFAULT_DURATION_MS.info,
  id: options?.id,
  dismissible: options?.dismissible,
  testId: options?.testId,
  unstyled: true,
})

export const toast = {
  success: ({ title, ...options }: ToastOptions) =>
    sonnerToast.success(title, buildToastOptions(options, DEFAULT_DURATION_MS.success)),
  info: ({ title, ...options }: ToastOptions) =>
    sonnerToast.info(title, buildToastOptions(options, DEFAULT_DURATION_MS.info)),
  error: ({ title, ...options }: ToastOptions) =>
    sonnerToast.error(title, buildToastOptions(options, DEFAULT_DURATION_MS.error)),
  custom: (
    content: ReactNode | ((id: number | string) => ReactElement),
    options?: ToastCustomOptions
  ) =>
    typeof content === "function"
      ? sonnerToast.custom(content, buildCustomOptions(options))
      : sonnerToast.custom(
          () => createElement(Fragment, null, content),
          buildCustomOptions(options)
        ),
  dismiss: (id?: number | string) => sonnerToast.dismiss(id),
}

export const useToast = () => toast
